import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, MenuItem, Select, Snackbar, Switch,
  TextField, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ReferencePhotoRow from './ReferencePhotoRow';
import { MAX_SECONDARY_REFERENCE_PHOTOS } from '../../config/appConfig';
import eventApi from './eventApi';

// Organiser-only editor for an event's global reference sets - named,
// independently toggleable face sets (or "All Photos" sets) that
// Smart Auto-Match folds into every photo request's match while enabled.
// Opened from a button inside AutoMatchToggle (EventPage.js).
export default function ReferenceSetsDialog({ open, onClose, eventId }) {
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'info' });

  function toast(msg, sev = 'info') {
    setSnack({ open: true, msg, sev });
  }

  async function reload() {
    setLoading(true);
    setError('');
    try {
      const res = await eventApi.listReferenceSets(eventId);
      setSets(Array.isArray(res?.sets) ? res.sets : []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load reference sets.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!open || !eventId) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, eventId]);

  function patchSetLocal(setId, patch) {
    setSets(prev => prev.map(s => (s.set_id === setId ? { ...s, ...patch } : s)));
  }

  async function handleAddSet() {
    setCreating(true);
    try {
      const created = await eventApi.createReferenceSet(eventId, 'New set', 'faces');
      setSets(prev => [...prev, { ...created, photos: created.photos || [] }]);
    } catch (e) {
      toast(e.response?.data?.error || e.message || 'Failed to create set.', 'error');
    } finally {
      setCreating(false);
    }
  }

  async function handleLabelBlur(set, value) {
    const label = value.trim() || 'Untitled set';
    if (label === set.label) return;
    patchSetLocal(set.set_id, { label });
    try {
      await eventApi.updateReferenceSet(eventId, set.set_id, { label });
    } catch (e) {
      toast('Failed to save name.', 'error');
    }
  }

  async function handleModeChange(set, mode) {
    patchSetLocal(set.set_id, { mode });
    try {
      await eventApi.updateReferenceSet(eventId, set.set_id, { mode });
    } catch (e) {
      toast('Failed to update mode.', 'error');
      patchSetLocal(set.set_id, { mode: set.mode });
    }
  }

  async function handleEnabledToggle(set, enabled) {
    patchSetLocal(set.set_id, { enabled });
    try {
      await eventApi.updateReferenceSet(eventId, set.set_id, { enabled });
      toast(`"${set.label}" turned ${enabled ? 'on' : 'off'}.`, 'success');
    } catch (e) {
      toast('Failed to update set.', 'error');
      patchSetLocal(set.set_id, { enabled: set.enabled });
    }
  }

  async function handleDeleteSet(set) {
    if (!window.confirm(`Delete reference set "${set.label}"?`)) return;
    try {
      await eventApi.deleteReferenceSet(eventId, set.set_id);
      setSets(prev => prev.filter(s => s.set_id !== set.set_id));
    } catch (e) {
      toast('Failed to delete set.', 'error');
    }
  }

  async function handleAddPhoto(set, file) {
    const result = await eventApi.addReferenceSetPhoto(eventId, set.set_id, file);
    patchSetLocal(set.set_id, {
      photos: [...(set.photos || []), { photo_key: result.photo_key, url: URL.createObjectURL(file), is_primary: result.is_primary }],
    });
  }

  async function handleRemovePhoto(set, photoKey) {
    await eventApi.deleteReferenceSetPhoto(eventId, set.set_id, photoKey);
    patchSetLocal(set.set_id, { photos: (set.photos || []).filter(p => p.photo_key !== photoKey) });
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reference Sets</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Face sets you turn on and off during the event - e.g. "Bride & Groom" for the ceremony,
          or a set with no photos flagged "All Photos" to share everything shot during a window with
          every request. Photos matched while a set is off aren't affected; turning it on only
          affects photos processed from then on.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
        ) : (
          <>
            {sets.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No reference sets yet.
              </Typography>
            )}
            {sets.map((set, idx) => (
              <Box key={set.set_id} sx={{ mb: 2 }}>
                {idx > 0 && <Divider sx={{ mb: 2 }} />}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    variant="standard"
                    defaultValue={set.label}
                    onBlur={(e) => handleLabelBlur(set, e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <Select
                    size="small"
                    variant="standard"
                    value={set.mode}
                    onChange={(e) => handleModeChange(set, e.target.value)}
                  >
                    <MenuItem value="faces">Faces</MenuItem>
                    <MenuItem value="all_photos">All Photos</MenuItem>
                  </Select>
                  <Switch
                    checked={Boolean(set.enabled)}
                    onChange={(e) => handleEnabledToggle(set, e.target.checked)}
                    size="small"
                  />
                  <IconButton size="small" onClick={() => handleDeleteSet(set)} aria-label="Delete set">
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
                {set.mode === 'faces' && (
                  <ReferencePhotoRow
                    photos={set.photos || []}
                    onAdd={(file) => handleAddPhoto(set, file)}
                    onRemove={(photoKey) => handleRemovePhoto(set, photoKey)}
                    maxCount={MAX_SECONDARY_REFERENCE_PHOTOS + 1}
                    label=""
                  />
                )}
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddSet}
              disabled={creating}
              sx={{ textTransform: 'none', mt: 1 }}
            >
              {creating ? 'Adding...' : 'Add set'}
            </Button>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
